import { Controller } from "@hotwired/stimulus";
import { h, options, render } from "preact";
import { islands } from "../islands";
import { LoadingMessage } from "../islands/ui/loading";

// Connects to data-controller="live-island" data-live-island-component-value="hello"
// data-live-island-props-value='{"start":1}'. Turbo caches the rendered markup, so a
// restored page connects onto stale children: connect clears them first. Every connect and disconnect bumps a generation, so only
// the latest connect renders when the lazy import resolves after a reconnect.
export default class extends Controller {
	static values = { component: String, props: { type: String, default: "{}" }, message: String };
	declare readonly componentValue: string;
	declare readonly propsValue: string;
	declare readonly messageValue: string;
	generation = 0;

	async connect() {
		const generation = ++this.generation;
		const load = islands[this.componentValue];
		if (!load) throw new Error(`live-island: no island named "${this.componentValue}"`);
		const { default: Component } = await load();
		if (generation !== this.generation) return;
		this.element.replaceChildren();
		render(h(LoadingMessage.Provider, { value: this.messageValue || undefined }, h(Component, JSON.parse(this.propsValue))), this.element);
	}

	disconnect() {
		this.unmount();
	}

	// Turbo snapshots the departing page before it disconnects; unmounting first keeps a popup's portal and its
	// `inert` on the page's other children out of the cached copy. connect() renders again on restore.
	unmount() {
		this.generation++;
		// Preact 11 runs passive cleanups after paint; an island's must finish before Turbo caches the page or the next island mounts.
		const schedule = options.requestAnimationFrame;
		const pending: (() => void)[] = [];
		const errors: unknown[] = [];
		const done = (step: () => void) => {
			try {
				step();
				return true;
			} catch (error) {
				errors.push(error);
				return false;
			}
		};
		options.requestAnimationFrame = (callback) => void pending.push(callback);
		done(() => render(null, this.element));
		options.requestAnimationFrame = schedule;
		// A cleanup that throws ends Preact's flush with the rest still queued; each call takes at least that one off the queue.
		for (const flush of pending) while (!done(flush));
		if (errors.length) throw errors[0];
	}
}
